import React from "react"
import { Link } from "react-router-dom"
import { Gauge, LayoutTemplate, MousePointerClick, Palette, Puzzle, Route, Search, Smartphone, Wrench } from "lucide-react"
import SEO from "../components/SEO.jsx"
import RelatedServices from "../components/page/RelatedServices.jsx"
import { CONTACT_PROJECT } from "../data/services.js"
import { FEATURED_PROJECTS, SCREENSHOT_SIZE } from "../data/portfolio.js"

// All enquiries go through /contact so its GA4 and Google Ads conversion
// tracking applies. Website development is preselected on the form.
const REDESIGN_ENQUIRY = `${CONTACT_PROJECT}&service=website-development`
// Shows the free-review heading on /contact; the submission carries enquiryIntent=review.
const REVIEW_ENQUIRY = "/contact?intent=review&service=website-development"

const PRIMARY_BUTTON = "inline-flex text-center min-h-[52px] items-center justify-center rounded-full bg-gradient-to-r from-brand-primary to-[#3498db] px-5 py-3 sm:px-7 text-[0.98rem] font-bold text-white shadow-button transition hover:-translate-y-[1px]"
const SECONDARY_BUTTON = "inline-flex text-center min-h-[48px] items-center justify-center rounded-full border border-[rgba(22,53,90,0.14)] bg-brand-surface px-6 py-3 text-[0.95rem] font-bold text-[#16355a] transition hover:-translate-y-[1px] hover:border-brand-primary"
const LIGHT_BUTTON = "inline-flex text-center min-h-[52px] items-center justify-center rounded-full bg-white px-5 py-3 sm:px-7 text-[0.98rem] font-bold text-brand-navy shadow-[0_16px_30px_rgba(0,0,0,0.18)] transition hover:-translate-y-[1px]"
const EYEBROW = "mb-4 text-[0.78rem] font-bold uppercase tracking-[0.16em] text-brand-primary"
const SECTION_HEADING = "mb-4 text-[clamp(2rem,3vw,3.3rem)] leading-[1.1] text-brand-heading"

const [CHERRY_CREEK, SPECTRUM, MIDWAY] = FEATURED_PROJECTS

const WARNING_SIGNS = [
  {
    icon: LayoutTemplate,
    title: "It looks dated",
    text: "The design no longer matches the quality of your products, services or team.",
  },
  {
    icon: Smartphone,
    title: "It is awkward on mobile",
    text: "Text is hard to read, buttons are fiddly and visitors have to pinch and zoom to get around.",
  },
  {
    icon: MousePointerClick,
    title: "Visitors are not sure what to do next",
    text: "There is no clear path from reading about your business to getting in touch.",
  },
  {
    icon: Wrench,
    title: "It is hard to keep up to date",
    text: "Simple changes take too long, so content and offers quickly fall behind.",
  },
  {
    icon: Puzzle,
    title: "It cannot do what you need",
    text: "Bookings, online sales, enquiry handling or connections to your systems are missing or held together with workarounds.",
  },
  {
    icon: Palette,
    title: "It no longer reflects your business",
    text: "Your services, positioning or brand have moved on, but the website still tells an old story.",
  },
]

const REDESIGN_OUTCOMES = [
  {
    icon: Palette,
    title: "Modern, professional design",
    text: "A considered visual design that presents your business with the same care you put into your work.",
  },
  {
    icon: Smartphone,
    title: "A better mobile experience",
    text: "Layouts, navigation and forms designed for phones first, so mobile visitors can read, browse and enquire easily.",
  },
  {
    icon: Route,
    title: "Clearer customer journeys",
    text: "Structure and calls to action that guide visitors from their first question to the right next step.",
  },
  {
    icon: Search,
    title: "SEO foundations",
    text: "Clean page structure, sensible metadata and fast loading pages that give search engines a clear picture of your business.",
  },
  {
    icon: Gauge,
    title: "Integrations with how you work",
    text: "Bookings, e-commerce, CRM and other business systems connected where they make day-to-day work easier.",
  },
]

const PROCESS_STEPS = [
  ["01", "Review", "We look at your current website, how customers use it and where it is holding your business back."],
  ["02", "Plan", "We agree the structure, content, features and integrations the new website needs."],
  ["03", "Design & build", "We design and develop the website with mobile usability, performance and clear journeys in mind."],
  ["04", "Launch", "We test, launch and talk through ongoing support and improvements that suit your business."],
]

function DeviceShot({ project, kind, className = "", priority = false }) {
  const mobile = kind === "mobile"
  const size = mobile ? SCREENSHOT_SIZE.mobile : SCREENSHOT_SIZE.desktop

  return (
    <img
      src={mobile ? project.mobile : project.desktop}
      alt={mobile ? project.mobileAlt : project.desktopAlt}
      width={size.width}
      height={size.height}
      loading={priority ? "eager" : "lazy"}
      fetchpriority={priority ? "high" : undefined}
      decoding="async"
      className={`block h-full w-full object-cover object-top ${className}`}
    />
  )
}

function BrowserFrame({ project, priority = false }) {
  return (
    <div className="overflow-hidden rounded-xl border border-white/60 bg-white shadow-[0_30px_70px_rgba(4,18,38,0.35)]">
      <div className="flex h-7 items-center gap-1.5 border-b border-brand-border bg-white px-3" aria-hidden="true">
        <span className="h-2 w-2 rounded-full bg-[#d4dce6]" />
        <span className="h-2 w-2 rounded-full bg-[#d4dce6]" />
        <span className="h-2 w-2 rounded-full bg-[#d4dce6]" />
        <span className="ml-2 truncate rounded bg-[#f1f4f8] px-2 py-0.5 text-[0.65rem] text-[#718399]">
          {new URL(project.url).hostname.replace(/^www\./, "")}
        </span>
      </div>
      <div className="aspect-[16/10] bg-[#eaf0f7]">
        <DeviceShot project={project} kind="desktop" priority={priority} />
      </div>
    </div>
  )
}

function PhoneFrame({ project, className = "", priority = false }) {
  return (
    <div className={`overflow-hidden rounded-[18px] border-[4px] border-[#193653] bg-white shadow-[0_24px_50px_rgba(4,18,38,0.4)] ${className}`}>
      <div className="aspect-[9/19] bg-[#eaf0f7]">
        <DeviceShot project={project} kind="mobile" priority={priority} />
      </div>
    </div>
  )
}

// Hero composition: one desktop view with two phones layered over it.
function HeroShowcase() {
  return (
    <div className="relative mx-auto w-full max-w-[560px] pb-10 lg:pb-14">
      <BrowserFrame project={SPECTRUM} priority />
      <PhoneFrame project={MIDWAY} priority className="absolute -bottom-2 right-2 w-[24%] sm:right-4 lg:-bottom-4" />
      <PhoneFrame project={CHERRY_CREEK} className="absolute -bottom-6 right-[27%] hidden w-[21%] sm:block lg:-bottom-8" />
    </div>
  )
}

// Alternating rows, so the work reads differently from the card grid on /web-design-adelaide.
function SelectedWork() {
  return (
    <section id="selected-work" className="bg-brand-bg py-section" aria-labelledby="selected-work-title">
      <div className="mx-auto max-w-[1180px] px-6">
        <div className="mb-12 max-w-[760px]">
          <p className={EYEBROW}>Selected work</p>
          <h2 id="selected-work-title" className={SECTION_HEADING}>
            Websites designed around the businesses behind them
          </h2>
          <p className="text-[1.05rem] leading-[1.75] text-brand-textSoft">
            A sample of recent 2merative websites, each built around how that business sells, books
            and communicates with its customers.
          </p>
        </div>

        <div className="space-y-14 lg:space-y-20">
          {FEATURED_PROJECTS.map((project, index) => (
            <article
              key={project.name}
              className="grid items-center gap-10 lg:grid-cols-[1.25fr_1fr] lg:gap-14"
            >
              <div className={`relative pb-8 ${index % 2 ? "lg:order-2" : ""}`}>
                <div className="rounded-xl3 bg-gradient-to-br from-[#eef3f9] to-[#dfe8f2] p-4 sm:p-6">
                  <BrowserFrame project={project} />
                </div>
                <PhoneFrame project={project} className="absolute bottom-0 right-5 w-[22%] max-w-[140px] sm:right-8" />
              </div>

              <div className={index % 2 ? "lg:order-1" : ""}>
                <p className="mb-2 text-xs font-bold uppercase tracking-[0.13em] text-brand-primary">{project.category}</p>
                <h3 className="mb-3 text-[1.6rem] font-bold leading-[1.2] text-brand-heading">{project.name}</h3>
                <p className="mb-3 text-[1.05rem] leading-[1.7] text-brand-textSoft">{project.summary}</p>
                <p className="mb-5 text-sm font-medium text-[#48617c]">{project.detail}</p>
                <a
                  href={project.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex min-h-[44px] items-center font-semibold text-brand-primary hover:underline"
                >
                  View live website <span aria-hidden="true" className="ml-2">↗</span>
                </a>
              </div>
            </article>
          ))}
        </div>

        <div className="mt-16 flex flex-col gap-6 rounded-xl3 bg-gradient-to-br from-brand-navy to-[#16406f] p-6 text-white shadow-[0_25px_60px_rgba(10,35,67,0.18)] sm:p-10 md:flex-row md:items-center md:justify-between md:gap-10">
          <h2 className="max-w-[620px] text-[clamp(1.5rem,2.4vw,2.1rem)] font-bold leading-[1.2] text-white">
            Ready to see what your business could look like online?
          </h2>
          <Link to={REDESIGN_ENQUIRY} className={`${LIGHT_BUTTON} w-full shrink-0 sm:w-auto`}>
            Discuss Your Website Redesign
          </Link>
        </div>
      </div>
    </section>
  )
}

export default function WebsiteRedesignAdelaide() {
  return (
    <div className="bg-brand-bg text-brand-text">
      <SEO
        title="Website Redesign Adelaide | Premium Website Redesign | 2merative"
        description="Website redesign Adelaide for businesses ready for a modern, professional website. Improve mobile experience, credibility and customer enquiries with 2merative."
        path="/website-redesign-adelaide"
        image="/og-image.png"
      />

      <section className="brand-hero pt-28 pb-section text-white">
        <div className="mx-auto max-w-[1180px] px-6">
          <div className="grid items-center gap-12 lg:grid-cols-[1.05fr_1fr] lg:gap-10">
            <div className="max-w-[640px]">
              <div className="flex h-8 items-center">
                <p className="brand-hero-pill">Website Redesign Adelaide</p>
              </div>

              <h1 className="m-0 mt-14 text-[clamp(2.5rem,5.2vw,4.4rem)] font-bold leading-[1.02] tracking-[-0.03em] text-white">
                Your business has evolved.
                <span className="block text-blue-100">Has your website kept up?</span>
              </h1>

              <p className="mt-6 max-w-[600px] text-[1.12rem] leading-[1.85] text-blue-50">
                Premium website redesigns for Adelaide businesses ready for a more professional online
                presence. Modern design, better mobile experiences and websites built around how your
                business operates today.
              </p>

              <div className="mt-8 flex flex-col gap-4 sm:flex-row sm:flex-wrap">
                <Link to={REDESIGN_ENQUIRY} className={PRIMARY_BUTTON}>
                  Discuss Your Website Redesign
                </Link>
                <Link to={REVIEW_ENQUIRY} className="inline-flex text-center min-h-[52px] items-center justify-center rounded-full border border-white/40 bg-white/10 px-6 py-3 text-[0.98rem] font-bold text-white transition hover:-translate-y-[1px] hover:bg-white/20">
                  Get a Free Website Review
                </Link>
              </div>
            </div>

            <HeroShowcase />
          </div>
        </div>
      </section>

      <section className="bg-white py-section" aria-labelledby="signs-title">
        <div className="mx-auto max-w-[1180px] px-6">
          <div className="mb-12 max-w-[760px]">
            <p className={EYEBROW}>Signs it is time</p>
            <h2 id="signs-title" className={SECTION_HEADING}>
              Is your current website holding your business back?
            </h2>
            <p className="text-[1.05rem] leading-[1.75] text-brand-textSoft">
              Websites rarely fail overnight. They slowly fall behind as a business grows, and the gap
              shows up in how customers see you.
            </p>
          </div>

          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {WARNING_SIGNS.map(({ icon: Icon, title, text }) => (
              <article key={title} className="rounded-xl2 border border-brand-border bg-brand-surface p-7 shadow-card">
                <span className="mb-5 flex h-11 w-11 items-center justify-center rounded-xl bg-brand-primarySoft text-brand-primary">
                  <Icon aria-hidden="true" className="h-5 w-5" />
                </span>
                <h3 className="mb-2 text-lg font-semibold text-brand-heading">{title}</h3>
                <p className="leading-[1.7] text-brand-textSoft">{text}</p>
              </article>
            ))}
          </div>

          <div className="mt-10 flex flex-col gap-5 border-t border-[rgba(22,53,90,0.12)] pt-8 sm:flex-row sm:items-center sm:justify-between">
            <h3 className="text-[1.2rem] font-semibold leading-[1.4] text-brand-heading">
              Recognise a few of these? A redesign could make a real difference.
            </h3>
            <Link to={REDESIGN_ENQUIRY} className={`${SECONDARY_BUTTON} w-full shrink-0 sm:w-auto`}>
              Talk About Your Website
            </Link>
          </div>
        </div>
      </section>

      <SelectedWork />

      <section className="bg-white py-section" aria-labelledby="outcomes-title">
        <div className="mx-auto grid max-w-[1180px] gap-12 px-6 lg:grid-cols-[0.85fr_1.15fr] lg:gap-16">
          <div className="lg:sticky lg:top-28 lg:self-start">
            <p className={EYEBROW}>What a redesign delivers</p>
            <h2 id="outcomes-title" className={SECTION_HEADING}>
              More than a new look
            </h2>
            <p className="text-[1.05rem] leading-[1.75] text-brand-textSoft">
              A redesign is a chance to rethink how your website works for customers and for your
              team, not just how it looks.
            </p>
          </div>

          <ul className="divide-y divide-brand-border">
            {REDESIGN_OUTCOMES.map(({ icon: Icon, title, text }) => (
              <li key={title} className="flex gap-5 py-6 first:pt-0 last:pb-0">
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-brand-primary to-[#3498db] text-white shadow-button">
                  <Icon aria-hidden="true" className="h-5 w-5" />
                </span>
                <div>
                  <h3 className="mb-1.5 text-xl font-semibold text-brand-heading">{title}</h3>
                  <p className="leading-[1.7] text-brand-textSoft">{text}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="bg-brand-bg py-section" aria-labelledby="process-title">
        <div className="mx-auto max-w-[1180px] px-6">
          <p className={EYEBROW}>Our redesign process</p>
          <h2 id="process-title" className={`${SECTION_HEADING} mb-10`}>From review to relaunch</h2>
          <ol className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {PROCESS_STEPS.map(([number, title, description]) => (
              <li key={number} className="rounded-xl2 border border-brand-border bg-white p-7 shadow-card">
                <span className="mb-4 block text-sm font-bold tracking-[0.14em] text-brand-primary">{number}</span>
                <h3 className="mb-3 text-xl font-semibold text-brand-heading">{title}</h3>
                <p className="leading-[1.7] text-brand-textSoft">{description}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="bg-white py-section" aria-labelledby="free-review-title">
        <div className="mx-auto grid max-w-[1180px] items-start gap-10 px-6 md:grid-cols-[1.2fr_1fr] md:gap-14">
          <div>
            <p className={EYEBROW}>Free website review</p>
            <h2 id="free-review-title" className={SECTION_HEADING}>
              Not sure if you need a redesign?
            </h2>
            <p className="mb-8 text-[1.05rem] leading-[1.75] text-brand-textSoft">
              Your website might need a complete rebuild, or a few targeted improvements could make a
              meaningful difference. Contact us for a free website review and we'll help you
              understand your options.
            </p>
            <Link to={REVIEW_ENQUIRY} className={`${SECONDARY_BUTTON} w-full sm:w-auto`}>
              Request My Free Website Review
            </Link>
          </div>

          <div className="rounded-xl2 border border-brand-border bg-brand-bg p-7 sm:p-8">
            <h3 className="mb-4 text-lg font-semibold text-brand-heading">Helpful to include</h3>
            <ul className="space-y-3 leading-[1.7] text-brand-textSoft">
              {[
                "Your current website address",
                "What is not working or feels out of date",
                "What you would like the website to help your business do",
              ].map(item => (
                <li key={item} className="relative pl-6 before:absolute before:left-0 before:top-[0.1rem] before:font-extrabold before:text-brand-primary before:content-['✓']">
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <RelatedServices exclude="website-development" title="Pair your redesign with" tone="muted" />

      <section className="bg-gradient-to-br from-[#1a56c0] to-[#2b7bd8] py-section text-white" aria-labelledby="final-cta-title">
        <div className="mx-auto max-w-[860px] px-6 text-center">
          <h2 id="final-cta-title" className="mb-5 text-[clamp(2rem,3.4vw,3.3rem)] font-bold leading-[1.1] text-white">
            Ready for a website that reflects your business today?
          </h2>
          <p className="mx-auto mb-9 max-w-[640px] text-[1.05rem] leading-[1.75] text-blue-50">
            Whether your current website needs a complete redesign or you're unsure what should
            change, let's discuss the best way forward.
          </p>
          <Link to={REDESIGN_ENQUIRY} className={`${LIGHT_BUTTON} w-full sm:w-auto`}>
            Discuss Your Website Redesign
          </Link>
          <p className="mt-6 text-[0.95rem] text-blue-50">
            Not sure you need a redesign?{" "}
            <Link to={REVIEW_ENQUIRY} className="font-semibold text-white underline underline-offset-4 hover:text-blue-50">
              Request a free website review
            </Link>
            .
          </p>
        </div>
      </section>
    </div>
  )
}
